import { Image, Text } from "@/atoms";
import { Icon } from "@/atoms/icon/Icon";
import { cn } from "@/lib/utils";
import ShareIcon from "atoms/icon/icons/share.svg";

type GiftCardPreviewProps = {
  recipientName: string;
  senderName: string;
  isSomeoneElse: boolean;
  onShareClick?: () => void;
  platformLogo: string;
  preview: {
    image: string;
    valueInCents: number;
  };
};

export const GiftCardPreview = ({
  recipientName,
  senderName,
  platformLogo,
  isSomeoneElse,
  onShareClick,
  preview,
}: GiftCardPreviewProps) => {
  const handleShareClick = () => {
    if (onShareClick) {
      onShareClick();
    }
  };

  const renderValue = () =>
    !!preview.valueInCents && (
      <div
        className={cn(
          "absolute bottom-2 left-2 z-10 hidden items-start justify-start  lg:flex"
        )}
      >
        <Text
          className={cn("relative !text-5xl font-bold text-white")}
          text="$"
          variant={"h1"}
        />
        <Text
          className={cn("relative !text-5xl font-bold text-white")}
          text={`${preview.valueInCents / 100}`}
          variant={"h1"}
        />
      </div>
    );

  const renderLogo = () => {
    return (
      <div className="absolute bottom-3 right-3 z-10 hidden h-12 w-12 rounded-full bg-white  p-0.5 lg:block">
        <Image
          src={platformLogo}
          alt="Platform logo"
          className="object-contain"
        />
      </div>
    );
  };

  const renderInfo = () => {
    return (
      <div className="bg-grey flex flex-1 flex-col justify-center rounded bg-opacity-60  lg:w-full lg:rounded-xl lg:bg-white  lg:shadow-sm">
        {isSomeoneElse && (
          <div className="hidden items-center justify-between p-2 lg:flex lg:p-4">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <Text
                  variant="body1"
                  text={`To:`}
                  component="p"
                  className="font-bold"
                />
                <Text variant="body1" text={`${recipientName}`} component="p" />
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Text
                  variant="body1"
                  text={`From:`}
                  component="p"
                  className="font-bold"
                />
                <Text variant="body1" text={`${senderName}`} component="p" />
              </div>
            </div>
            <Text
              variant="h1"
              text={`$${preview.valueInCents / 100}`}
              component="p"
              className="font-bold"
            />
          </div>
        )}

        <div className="flex flex-col lg:hidden">
          <Text
            variant="h2"
            text={`$${preview.valueInCents / 100}`}
            component="p"
            className="font-bold"
          />
          {isSomeoneElse && (
            <>
              <div className="flex flex-wrap items-center gap-0.5">
                <Text
                  variant="body3"
                  text={`To:`}
                  component="p"
                  className="font-bold"
                />
                <Text variant="body3" text={`${recipientName}`} component="p" />
              </div>
              <div className="flex flex-wrap items-center gap-0.5">
                <Text
                  variant="body3"
                  text={`From:`}
                  component="p"
                  className="font-bold"
                />
                <Text variant="body3" text={`${senderName}`} component="p" />
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderTile = () => {
    return (
      <div
        className={cn(
          "relative flex max-h-[200px] flex-1 items-center justify-center overflow-hidden rounded-md lg:max-h-none lg:w-full lg:flex-none lg:rounded-xl"
        )}
      >
        <Image
          alt="Gift Card background"
          className="aspect-[16/9] rounded-md object-cover shadow-sm lg:h-full lg:w-full lg:rounded-xl"
          src={preview.image || ""}
          width={170}
          height={110}
        />

        {renderValue()}
        {renderLogo()}
      </div>
    );
  };

  return (
    <div className="GiftCardPreview flex  max-h-[200px] w-full gap-2  p-4 sm:max-w-xl lg:flex lg:max-h-none  lg:w-full lg:flex-col lg:gap-4">
      {renderTile()}

      {renderInfo()}

      <div className="flex items-center justify-center lg:hidden">
        <button
          type="button"
          onClick={handleShareClick}
          className="flex flex-shrink-0 cursor-pointer items-center justify-center rounded-full p-3 outline outline-1 outline-offset-[-1px] outline-gray-400 transition-colors hover:bg-gray-50 lg:hidden"
        >
          <Icon svg={ShareIcon} className="h-[20px] w-[20px]" />
        </button>
      </div>
    </div>
  );
};

export const GiftCardPreviewAppOnly = ({
  recipientName,
  senderName,
  platformLogo,
  isSomeoneElse,
  onShareClick,
  preview,
}: GiftCardPreviewProps) => {
  const handleShareClick = () => {
    if (onShareClick) {
      onShareClick();
    }
  };

  const renderValue = () =>
    !!preview.valueInCents && (
      <div
        className={cn(
          "absolute bottom-2 left-2 z-10 hidden items-start justify-start"
        )}
      >
        <Text
          className={cn("relative !text-5xl font-bold text-white")}
          text="$"
          variant={"h1"}
        />
        <Text
          className={cn("relative !text-5xl font-bold text-white")}
          text={`${preview.valueInCents / 100}`}
          variant={"h1"}
        />
      </div>
    );

  const renderLogo = () => {
    return (
      <div className="absolute bottom-3 right-3 z-10 hidden h-12 w-12 rounded-full bg-white object-contain p-0.5">
        <Image src={platformLogo} alt="Platform logo" />
      </div>
    );
  };

  const renderInfo = () => {
    return (
      <div className="bg-grey flex flex-1 flex-col justify-center rounded bg-opacity-60 p-2">
        <div className="flex flex-col">
          <Text
            variant="h2"
            text={`$${preview.valueInCents / 100}`}
            component="p"
            className="font-bold"
          />
          {isSomeoneElse && (
            <>
              <div className="flex flex-wrap items-center gap-0.5">
                <Text
                  variant="body3"
                  text={`To:`}
                  component="p"
                  className="font-bold"
                />
                <Text variant="body3" text={`${recipientName}`} component="p" />
              </div>
              <div className="flex flex-wrap items-center gap-0.5">
                <Text
                  variant="body3"
                  text={`From:`}
                  component="p"
                  className="font-bold"
                />
                <Text variant="body3" text={`${senderName}`} component="p" />
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderTile = () => {
    return (
      <div
        className={cn(
          "relative flex max-h-[200px] flex-1 items-center justify-center overflow-hidden rounded-md"
        )}
      >
        <Image
          alt="Gift Card background"
          className="aspect-[16/9] rounded-md object-cover shadow-sm"
          src={preview.image || ""}
          width={170}
          height={110}
        />

        {renderValue()}
        {renderLogo()}
      </div>
    );
  };

  return (
    <div className="GiftCardPreviewAppOnly flex max-h-[200px] gap-2 bg-gray-100 p-2 sm:max-w-xl">
      {renderTile()}

      {renderInfo()}

      <div className="flex items-center justify-center">
        <button
          type="button"
          onClick={handleShareClick}
          className="flex flex-shrink-0 cursor-pointer items-center justify-center rounded-full p-3 outline outline-1 outline-offset-[-1px] outline-gray-400 transition-colors hover:bg-gray-50"
        >
          <Icon svg={ShareIcon} className="h-[20px] w-[20px]" />
        </button>
      </div>
    </div>
  );
};
